import { HISTORICAL_TIMEFRAMES } from "../api/insightsApi";

/*
 * Which historical timeframes are worth offering. A longer timeframe is listed
 * only when it reaches calendar weeks with training data that the previous
 * listed (shorter) timeframe does not already cover, so the menu never offers
 * two horizons with the same history.
 *
 * Read from the ALL_TIME adherence response, which already lists every
 * calendar week from the user's first data with its counts: a week "has data"
 * when anything was planned or logged in it (the same facts the backend uses
 * for the start of ALL_TIME). Window bounds mirror the backend's
 * historicalTimeframes.js: the Monday of the week containing
 * (today − N months), month subtraction clamped to the month's last day.
 * Nothing here changes what a timeframe computes; it only filters the menu.
 */

const TIMEFRAME_MONTHS = Object.freeze({
  "3M": 3,
  "6M": 6,
  "1Y": 12,
  "2Y": 24,
  "5Y": 60,
  "10Y": 120,
});

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

function toDate(dateKey) {
  return new Date(`${dateKey}T00:00:00.000Z`);
}

function toKey(date) {
  return date.toISOString().slice(0, 10);
}

export function subtractMonths(dateKey, months) {
  const date = toDate(dateKey);
  const month = date.getUTCMonth() - months;
  const year = date.getUTCFullYear() + Math.floor(month / 12);
  const targetMonth = ((month % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
  return toKey(new Date(Date.UTC(year, targetMonth, Math.min(date.getUTCDate(), lastDay))));
}

export function mondayOf(dateKey) {
  const date = toDate(dateKey);
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return toKey(date);
}

function hasCount(metric) {
  if (!metric) return false;
  return ["due", "completed", "extra", "notYetDue"].some((field) => Number(metric[field]) > 0);
}

export function weekHasData(week) {
  return hasCount(week?.workouts) || hasCount(week?.sets);
}

function windowStart(timeframe, today) {
  return mondayOf(subtractMonths(today, TIMEFRAME_MONTHS[timeframe]));
}

// null when the response cannot answer (not loaded, unexpected shape): the
// caller then offers every timeframe rather than hiding one wrongly.
export function availableTimeframes(allTimeResponse) {
  const today = allTimeResponse?.range?.today;
  if (typeof today !== "string" || !DATE_KEY.test(today) || !Array.isArray(allTimeResponse?.weeks)) return null;

  const dataWeeks = allTimeResponse.weeks
    .filter(weekHasData)
    .map((week) => week.startDate)
    .filter((startDate) => typeof startDate === "string" && DATE_KEY.test(startDate));
  const covered = (timeframe) => (timeframe in TIMEFRAME_MONTHS
    ? dataWeeks.filter((startDate) => startDate >= windowStart(timeframe, today)).length
    : dataWeeks.length);

  const [shortest, ...longer] = HISTORICAL_TIMEFRAMES;
  const available = [shortest];
  let coveredSoFar = covered(shortest);
  longer.forEach((timeframe) => {
    const count = covered(timeframe);
    if (count > coveredSoFar) {
      available.push(timeframe);
      coveredSoFar = count;
    }
  });
  return available;
}
